import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LifebuoyIcon, ChevronLeftIcon, PaperAirplaneIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { useSupportThreads, useSupportThread, useReplySupport, useResolveSupport } from '../hooks/useSupport';

const fmtTime = (d) => (d ? new Date(d).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '');
const statusPill = (s) =>
  (s === 'resolved' ? 'bg-emerald-100 text-emerald-700' : s === 'assigned' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700');

// The selected conversation: the engineer's messages left, responder/bot right,
// with a reply composer and a resolve control.
function ThreadPane({ id }) {
  const { data, isLoading } = useSupportThread(id);
  const reply = useReplySupport();
  const resolve = useResolveSupport();
  const [text, setText] = useState('');

  if (!id) return <div className="flex h-full items-center justify-center px-4 text-center text-sm text-gray-400">Select a conversation from the list to reply.</div>;
  if (isLoading || !data) return <p className="p-4 text-sm text-gray-400">Loading…</p>;

  const { thread, messages } = data;
  const onSend = async (e) => {
    e.preventDefault();
    const body = text.trim();
    if (!body || reply.isPending) return;
    setText('');
    try { await reply.mutateAsync({ id, body }); } catch (err) { setText(body); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between gap-2 border-b border-gray-200 bg-white px-4 py-3">
        <div className="min-w-0">
          <div className="truncate font-semibold text-gray-900">{thread.engineer_name || 'Engineer'}</div>
          <div className="truncate text-xs text-gray-400">{thread.engineer_email}</div>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${statusPill(thread.status)}`}>{thread.status}</span>
          {thread.status !== 'resolved' && (
            <button type="button" onClick={() => resolve.mutate(id)} disabled={resolve.isPending}
              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2.5 py-1 text-xs font-medium text-gray-600 hover:border-emerald-400 hover:text-emerald-600 disabled:opacity-50">
              <CheckCircleIcon className="h-4 w-4" /> Resolve
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 space-y-2 overflow-y-auto p-4">
        {messages.map((m) => {
          const eng = m.sender_type === 'engineer';
          const who = m.sender_type === 'bot' ? 'Nano Bot' : (m.sender_name || 'Support');
          return (
            <div key={m.id} className={`flex ${eng ? 'justify-start' : 'justify-end'}`}>
              <div className={`max-w-[75%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${eng ? 'rounded-bl-sm border border-gray-200 bg-white text-gray-800' : 'rounded-br-sm bg-brand text-white'}`}>
                <div className={`mb-0.5 text-[11px] font-semibold ${eng ? 'text-gray-500' : 'text-white/80'}`}>{eng ? (thread.engineer_name || 'Engineer') : who}</div>
                <div className="whitespace-pre-wrap">{m.body}</div>
                <div className={`mt-0.5 text-[10px] ${eng ? 'text-gray-400' : 'text-white/70'}`}>{fmtTime(m.created_at)}</div>
              </div>
            </div>
          );
        })}
      </div>

      <form onSubmit={onSend} className="border-t border-gray-200 bg-white p-3">
        <div className="flex items-end gap-2">
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(e); } }}
            rows={1}
            placeholder="Reply to the engineer…"
            className="max-h-40 min-h-[42px] flex-1 resize-none rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
          />
          <button type="submit" disabled={!text.trim() || reply.isPending}
            className="flex h-10 items-center gap-1.5 rounded-lg bg-brand px-4 text-sm font-medium text-white hover:bg-brand-dark disabled:opacity-40">
            <PaperAirplaneIcon className="h-4 w-4" /> Send
          </button>
        </div>
      </form>
    </div>
  );
}

// Responder console: queue of engineer conversations on the left, the selected
// conversation on the right. Live assistance — the human side of Support (the
// Nano Bot answers first in Phase 2, escalating into these same threads).
function SupportConsole() {
  const navigate = useNavigate();
  const { data: threads, isLoading } = useSupportThreads();
  const [selected, setSelected] = useState(null);

  return (
    <div className="p-6">
      <button type="button" onClick={() => navigate('/?dept=support')} className="mb-3 inline-flex items-center gap-1 text-sm font-medium text-brand hover:text-brand-dark">
        <ChevronLeftIcon className="h-4 w-4" /> Back to Support
      </button>
      <h1 className="mb-1 flex items-center gap-2 text-2xl font-bold text-gray-900">
        <LifebuoyIcon className="h-7 w-7 text-brand" /> Support Console
      </h1>
      <p className="mb-5 text-sm text-gray-500">Live-assistance requests from engineers. Reply in-thread — the engineer is notified in their app.</p>

      <div className="grid gap-4 lg:grid-cols-[320px_1fr]">
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-100 px-3 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Conversations</div>
          <div className="max-h-[70vh] divide-y divide-gray-100 overflow-y-auto">
            {isLoading && <p className="px-3 py-4 text-sm text-gray-400">Loading…</p>}
            {threads && threads.length === 0 && <p className="px-3 py-6 text-center text-sm text-gray-400">No requests yet.</p>}
            {(threads || []).map((t) => (
              <button key={t.id} type="button" onClick={() => setSelected(t.id)}
                className={`block w-full px-3 py-2.5 text-left hover:bg-brand-light/40 ${selected === t.id ? 'bg-brand-light/60' : ''}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium text-gray-800">{t.engineer_name || 'Engineer'}</span>
                  {t.unread > 0 && <span className="flex h-5 min-w-[20px] shrink-0 items-center justify-center rounded-full bg-red-500 px-1 text-[11px] font-bold text-white">{t.unread}</span>}
                </div>
                <div className="mt-0.5 flex items-center justify-between gap-2">
                  <span className="truncate text-xs text-gray-400">{t.last_body}</span>
                  <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-medium ${statusPill(t.status)}`}>{t.status}</span>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-[60vh] rounded-lg border border-gray-200 bg-gray-50">
          <ThreadPane id={selected} />
        </div>
      </div>
    </div>
  );
}

export default SupportConsole;
